import queryKeys from "@configs/react-query/query-key";
import useSuccessToast from "@configs/react-toastify/snackbar";
import { useTheme } from "@hooks/useTheme";
import { useEffect, useState, useRef, useCallback  } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { formatDateForRequest, formatDateTime } from "@utils/common";
import { useMutationData } from "@hooks/useMutationData";

import AdvancedSearch from "@components/Search/AdvancedSearch";
import { useAdvancedSearch } from "@hooks/useAdvancedSearch";
import { resolveSearchCriteria } from "@utils/searchHelpers";
import TableHeaderControls from "@components/Project/TableHeaderControls";
import TableWithPagination from "@components/Tables/TableWithPagination";
import { useSearchInfo } from "@hooks/useSearchInfo";
import { TOAST } from "@constants/index";
import { ITEMS_PER_PAGE_KEY, DEFAULT_ITEMS_PER_PAGE } from "@constants/table";
import useItemsPerPage from "@hooks/useItemsPerPage";



const WorkHistoryList = () => {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const navigate = useNavigate();

  const debounceTimerRef = useRef(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [searchCriteria, setSearchCriteria] = useState({});

  const [debouncedSearchCriteria, setDebouncedSearchCriteria] = useState(searchCriteria);

  const [isSearching, setIsSearching] = useState(false);
  const [totalData, setTotalData] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const { itemsPerPage, handleItemsPerPageChange } = useItemsPerPage(ITEMS_PER_PAGE_KEY.WORK_HISTORY_LIST, DEFAULT_ITEMS_PER_PAGE);
  const [workHistoryList, setWorkHistoryList] = useState([]);
  
  const { creatorIdList, workTypeList, entityTypeList, fetchSearchInfo } = useSearchInfo();


  useEffect(() => {
    fetchSearchInfo();
  }, []);

  // Debounce search input to avoid calling API too frequently
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Show loading when user is typing (only when there's actual change)
    const hasActualChange = Object.values(searchCriteria).some((value) => value && value !== "");
    if (hasActualChange) {
      setIsSearching(true);
    }

    debounceTimerRef.current = setTimeout(() => {
      // Batch page reset with criteria commit so fetch never keeps the old startRow.
      setCurrentPage(1);
      setDebouncedSearchCriteria(searchCriteria);
      setIsSearching(false);
    }, 500);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchCriteria]);

  // Search fields for advanced search
  const searchFields = [
    {
      name: "workTypeCds",
      label: t("work-history.work-type"),
      type: "multiselect",
      options: workTypeList,
      placeholder: t("work-history.select-work-type"),
    },
    {
      name: "entityTypes",
      label: t("work-history.entity-type"),
      type: "multiselect",
      options: entityTypeList,
      placeholder: t("work-history.select-entity-type"),
    },
    {
      name: "workerId",
      label: t("work-history.worker-id"),
      type: "select",
      options: creatorIdList,
      placeholder: t("work-history.select-worker-id"),
    },
    { 
      name: "identificationNo", 
      label: t("work-history.location"), 
      placeholder: t("work-history.location") 
    },
    {
      name: "workedAtFrom",
      label: t("work-history.worked-at-from"),
      placeholder: t("work-history.worked-at-from"),
      type: "date",
    },
    {
      name: "workedAtTo",
      label: t("work-history.worked-at-to"),
      placeholder: t("work-history.worked-at-to"),
      type: "date",
    },
  ];
  const { showAdvancedSearch, handleAdvancedSearchToggle: toggleAdvancedSearch } = useAdvancedSearch({
    searchFields: searchFields,
    quickSearchField: "description",
    setSearchCriteria: setSearchCriteria,
  });

  const handleAdvancedSearchToggle = useCallback(() => {
    setCurrentPage(1);
    toggleAdvancedSearch();
  }, [toggleAdvancedSearch]);

  // API hook
  const getListData = useMutationData(
    queryKeys.getWorkHistoryList,
    {
      method: "POST",
    },
    {
      onSuccess: (response) => {
        if (typeof response !== "string" && response?.data) {
          setWorkHistoryList(response.data);
          setTotalData(response.total);
          setTotalPages(Math.ceil(response.total / itemsPerPage));
        }
      },
      onError: (error) => {
        console.error("Error during get data:", error);
        useSuccessToast(TOAST.error, t("common.error"), t("work-history.fetch-error"));
      },
    },
  );

  const fetchData = () => {
    const effectiveCriteria = resolveSearchCriteria(debouncedSearchCriteria, {
      quickSearchField: "description",
      isAdvancedPanelOpen: showAdvancedSearch,
    });

    const bodyRequest = Object.keys(effectiveCriteria).reduce((acc, key) => {
      let value = effectiveCriteria[key];
      if (typeof value === "string") {
        value = value.trim();
      }
      if (Array.isArray(value)) {
        value = value.map(o => o.value);
      }
      if (value !== "" && value !== undefined && value !== null) {
        acc[key] = formatDateForRequest(value);
      }
      return acc;
    }, {});
    bodyRequest["pageSize"] = itemsPerPage;
    bodyRequest["startRow"] = itemsPerPage * (currentPage - 1);
    getListData.mutate(bodyRequest);
  };

  // currentPage is reset in the same tick as debouncedSearchCriteria (see debounce above),
  // so this effect always sees startRow = 0 when search conditions change.
  useEffect(() => {
    fetchData();
  }, [currentPage, itemsPerPage, debouncedSearchCriteria, showAdvancedSearch]);

  const searchHandler = useCallback((inputData) => {
    setSearchCriteria((prev) => ({
      ...prev,
      ...inputData,
    }));
  }, []);

  const onRowClick = (rowData) => {
    navigate(`./${rowData.historySeq}`);
  };

  const handleItemsPerPageChangeWithReset = (newItemsPerPage) => {
    handleItemsPerPageChange(newItemsPerPage);
    setCurrentPage(1); // Reset to page 1 when changing number of rows
  };

  const columns = ["workTypeName", "description", "identificationNo", "entityTypeName", "workerId", "workedAt"];
  const columnConfig = {
    workTypeName: {
      style: {
        width: "15%",
        whiteSpace: "normal",
        overflowWrap: "initial",
      },
      headerRender: () => t("work-history.work-type"),
      customRender: (value, row) => row.workTypeName || "---",
    },
    description: {
      style: {
        minWidth: "140px",
        whiteSpace: "normal",
        overflowWrap: "initial",
      },
      headerRender: () => t("work-history.description"),
      customRender: (value, row) => row.description || "---",
    },
    identificationNo: {
      style: {
        width: "18%",
        whiteSpace: "normal",
        overflowWrap: "initial",
      },
      headerRender: () => t("work-history.location"),
      customRender: (value, row) => row.identificationNo || "---",
    },
    entityTypeName: {
      style: {
        width: "10%",
        whiteSpace: "normal",
        overflowWrap: "initial",
      },
      headerRender: () => t("work-history.entity-type"),
      customRender: (value, row) => row.entityTypeName || "---",
    },
    workerId: {
      style: {
        width: "9%",
        whiteSpace: "normal",
        overflowWrap: "initial",
      },
      headerRender: () => t("work-history.worker-id"),
      customRender: (value, row) => row.workerId || "---",
    },
    workedAt: {
      style: {
        width: "10%",
        whiteSpace: "normal",
        overflowWrap: "initial",
      },
      headerRender: () => t("work-history.worked-at"),
      customRender: (value, row) => {
        const formattedDate = formatDateTime(row.workedAt) || "---";
        if (formattedDate === "---") {
          return <div className={`font-normal ${colors.textMessage}`}>{formattedDate}</div>;
        }

        // Split date and time
        const [datePart, timePart] = formattedDate.split(" ");

        return (
          <div className={`font-normal ${colors.textMessage}`}>
            <div className="truncate" title={datePart}>
              {datePart != "-" ? datePart : "---"}
            </div>
            <div className="truncate" title={timePart}>
              {timePart != "-" ? timePart : "---"}
            </div>
          </div>
        );
      },
    },
  };

  return (
    <div className={`flex flex-col ${colors.bgSecondary}`} data-testid="work-history-list-page">
      <div className="flex justify-between items-center mb-4">
        <h1 className={`text-[28px] font-bold ${colors.textPrimary} uppercase`}>{t("work-history.work-history-title")}</h1>
      </div>

      {/* Controls */}
      <div className={`flex-shrink-0 pt-4 ${colors.bgSecondary}`}>
        <TableHeaderControls
          onAdvancedSearchToggle={handleAdvancedSearchToggle}
          searchField="description"
          searchValue={searchCriteria.description ?? ""}
          onSearch={searchHandler}
          placeholderSearch={t("common.search-placeholder")}
          isDisplayAddButton={false}
          isDisplayDeleteButton={false}
          isSearching={isSearching}
          searchInputTestId="work-history-list-search-input"
        />
      </div>

      {/* Advanced Search */}
      {showAdvancedSearch && (
        <div className="mb-4" data-testid="work-history-list-advanced-search">
          <AdvancedSearch
            title={t("project.advancedSearch")}
            searchFields={searchFields}
            searchHandler={searchHandler}
            withoutSearch={true}
          />
        </div>
      )}

      <div className="flex-grow min-h-0" data-testid="work-history-list-table">
        <TableWithPagination
          columnConfig={columnConfig}
          data={workHistoryList}
          columns={columns}
          onRowClick={onRowClick}
          loading={getListData.isPending}
          totalPages={totalPages}
          totalData={totalData}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          maxRowsPerPage={itemsPerPage}
          onItemsPerPageChange={handleItemsPerPageChangeWithReset}
          disabledRow={[]}
          hasRadioButton={false}
          rowKey="workHistorySeq"
          shouldShowGoToPage={true}
          showItemsPerPageSelector={true}
        />
      </div>
    </div>
  )
}

export default WorkHistoryList